export type Topic =
  | "Referrals"
  | "Scheduling"
  | "eConsult"
  | "Case Management"
  | "Cell Therapy"
  | "Public Health"
  | "Probation/Correctional Health"
  | "AI & Automation"
  | "Interoperability"

export interface WhitePaper {
  id: string
  title: string
  subtitle: string
  author: string
  date: string // ISO format for sorting
  displayDate: string // Formatted date for display
  readTime: string
  pdfUrl: string
  heroGradient: string
  topics: Topic[]
  featured?: boolean
}

export const WHITE_PAPERS: WhitePaper[] = [
  {
    id: "wp-011",
    title: "The Future of Referral Management in Healthcare: From Fax to AI-Powered Workflows",
    subtitle: "Referrals remain the 'front door' to specialty care, yet referral processes are often outdated, inefficient, and error-prone. Patients frequently experience delays, lost information, or misdirected referrals.",
    author: "EceptionistCX Research Group",
    date: "2025-08-26",
    displayDate: "August 26, 2025",
    readTime: "12 min read",
    pdfUrl: "/PDF/WhitePapers/ReferralCX_WhitePaper.pdf",
    heroGradient: "bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]",
    topics: ["Referrals", "Scheduling", "eConsult", "Case Management", "Cell Therapy", "Public Health", "Probation/Correctional Health", "AI & Automation", "Interoperability"],
    featured: true,
  },
  {
    id: "wp-009",
    title: "AI in Healthcare Operations: Practical Applications for Smarter Collaboration and Better Care",
    subtitle: "Exploring practical applications of AI in referral management, scheduling, bed management, staffing, and case prioritization to optimize resources and improve patient flow.",
    author: "Eceptionist Research Group",
    date: "2025-08-25",
    displayDate: "August 25, 2025",
    readTime: "12 min read",
    pdfUrl: "/PDF/WhitePapers/AI_HealthcareOps_WhitePaper.pdf",
    heroGradient: "bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]",
    topics: ["Referrals", "Scheduling", "Interoperability", "AI & Automation", "Case Management"],
  },
  {
    id: "wp-010",
    title: "Digital Front Doors in Healthcare: Why Scheduling Matters More Than Ever",
    subtitle: "Healthcare scheduling has emerged as one of the most strategic enablers of patient satisfaction, access to care, and provider efficiency. Patients now expect a seamless, consumer-grade digital experience.",
    author: "EceptionistCX Research Group",
    date: "2025-08-25",
    displayDate: "August 25, 2025",
    readTime: "10 min read",
    pdfUrl: "/PDF/WhitePapers/SchedulingCX_WhitePaper.pdf",
    heroGradient: "bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]",
    topics: ["Referrals", "Scheduling", "AI & Automation", "Interoperability"],
  },
  {
    id: "wp-012",
    title: "From Paper to Digital: Smarter Referrals for Justice-Involved Individuals",
    subtitle: "Probation agencies across the country still rely on paper-based, manual processes to refer justice-involved individuals to treatment, education, and support programs.",
    author: "EceptionistCX Research Group",
    date: "2025-08-25",
    displayDate: "August 25, 2025",
    readTime: "10 min read",
    pdfUrl: "/PDF/WhitePapers/ProbationCX_WhitePaper.pdf",
    heroGradient: "bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]",
    topics: ["Referrals", "Scheduling", "Probation/Correctional Health", "Interoperability"],
  },
  {
    id: "wp-013",
    title: "Breaking Silos: How Integrated Care Coordination Improves Outcomes and Reduces Costs",
    subtitle: "Fragmentation remains one of the greatest challenges in modern healthcare. Patients often navigate across multiple providers and services with little coordination.",
    author: "EceptionistCX Research Group",
    date: "2025-08-25",
    displayDate: "August 25, 2025",
    readTime: "11 min read",
    pdfUrl: "/PDF/WhitePapers/IntegratedCare_WhitePaper.pdf",
    heroGradient: "bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]",
    topics: ["Referrals", "Scheduling", "eConsult", "Case Management", "Cell Therapy", "Public Health", "AI & Automation", "Interoperability"],
  },
  {
    id: "wp-014",
    title: "Scaling Cell & Gene Therapy Programs: How Technology Enables Safe, Efficient, and Compliant Workflows",
    subtitle: "Cell and gene therapies, including CAR-T treatments, represent one of the fastest-growing areas of medicine. Yet scaling these programs outside of large academic medical centers remains a challenge.",
    author: "EceptionistCX Research Group",
    date: "2025-08-25",
    displayDate: "August 25, 2025",
    readTime: "15 min read",
    pdfUrl: "/PDF/WhitePapers/CellCX_WhitePaper.pdf",
    heroGradient: "bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]",
    topics: ["Referrals", "Scheduling", "Cell Therapy", "AI & Automation", "Interoperability"],
  },
]

// Helper function to format dates consistently
export function formatDate(iso: string): string {
  // Parse the date string and create a local date to avoid timezone issues
  const [year, month, day] = iso.split('-').map(Number)
  const date = new Date(year, month - 1, day) // month is 0-indexed
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  })
}

// Helper function to get white paper by ID
export function getWhitePaperById(id: string): WhitePaper | undefined {
  return WHITE_PAPERS.find(paper => paper.id === id)
}

// Helper function to get featured white papers
export function getFeaturedWhitePapers(): WhitePaper[] {
  return WHITE_PAPERS.filter(paper => paper.featured)
}
